import { isRequestError, createError, RequestError } from '@adv-frontend/error';
import type { BaseQueryFn } from './rtk-query-types';

/**
 * Тип ответа из modules-request.
 * Не можем заимпортить его из пакета, так как получается циклическая зависимость между пакетами.
 */
export interface Response {
    /**
     * HTTP статус
     */
    status?: number;
    /**
     * HTTP заголовки ответа
     */
    headers?: Record<string, string | string[] | undefined>;
    /**
     * Строка из HTTP ответа
     */
    body?: string;
    /**
     * Распаршенные данные из body.
     */
    data?: unknown;
}

/**
 * Опции запроса, которые требует RTK Query.
 */
export interface BaseQueryRequestOptions {
    signal?: AbortSignal;
}

/**
 * Функция request из пакета modules-request.
 */
type RequestFn<Options extends BaseQueryRequestOptions> = (options: Options) => Promise<Response>;

/**
 * Сериализованная ошибка запроса.
 */

/**
 * Мета информация для RTK Query;
 */
export interface BaseQueryMeta {
    response?: Response;
}

export const BaseQueryError = createError<undefined>('BaseQueryError');
export type BaseQueryError = InstanceType<typeof BaseQueryError>;

export type QueryFn<RequestOptions extends BaseQueryRequestOptions> = BaseQueryFn<
    RequestOptions,
    unknown,
    BaseQueryError | RequestError,
    unknown,
    BaseQueryMeta
>;

/**
 * Создает baseQuery функцию для инстанса modules-request.
 *
 * @param request Инстанс modules-request
 * @returns baseQuery функция
 */
export function createBaseQuery<RequestOptions extends BaseQueryRequestOptions>(
    request: RequestFn<RequestOptions>
): QueryFn<RequestOptions> {
    return async (arg, api) => {
        const meta: BaseQueryMeta = {};

        let response;
        try {
            response = await request({ ...arg, signal: api.signal });
            meta.response = response;

            return { data: response.data, meta };
        } catch (error) {
            if (isRequestError(error)) {
                meta.response = {
                    status: error.response?.status,
                    data: error.responseData,
                    headers: error.response?.headers,
                    body: typeof error.responseData === 'string' ? error.responseData : '',
                };

                return {
                    error,
                    meta,
                };
            }

            const baseQueryError = new BaseQueryError('Failed to call baseQuery', {
                cause: error,
            });

            return {
                error: baseQueryError,
                meta,
            };
        }
    };
}
